import axios from 'axios';

/** Categorias de falha que a UI precisa distinguir. */
export type ApiErrorKind = 'unauthorized' | 'notFound' | 'validation' | 'network' | 'server' | 'unknown';

/**
 * Erro normalizado da API.
 *
 * Toda a camada de serviços rejeita com este tipo, para que os componentes
 * nunca precisem inspecionar `error.response?.status`.
 */
export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number | null;

  constructor(kind: ApiErrorKind, message: string, status: number | null) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
  }

  get isUnauthorized(): boolean {
    return this.kind === 'unauthorized';
  }
}

const MESSAGES: Record<ApiErrorKind, string> = {
  unauthorized: 'Sessão expirada. Faça login novamente.',
  notFound: 'Registro não encontrado.',
  validation: 'Dados inválidos. Revise as informações e tente novamente.',
  network: 'Não foi possível falar com o servidor. Verifique a conexão.',
  server: 'O servidor não respondeu como esperado. Tente novamente em instantes.',
  unknown: 'Algo não deu certo. Tente novamente.',
};

function kindFromStatus(status: number): ApiErrorKind {
  if (status === 401 || status === 403) return 'unauthorized';
  if (status === 404) return 'notFound';
  if (status === 400 || status === 422) return 'validation';
  if (status >= 500) return 'server';
  return 'unknown';
}

/** Converte qualquer exceção (axios ou não) em `ApiError`. */
export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;

  if (axios.isAxiosError(error)) {
    const status = error.response?.status ?? null;
    if (status === null) {
      return new ApiError('network', MESSAGES.network, null);
    }
    const kind = kindFromStatus(status);
    return new ApiError(kind, MESSAGES[kind], status);
  }

  return new ApiError('unknown', MESSAGES.unknown, null);
}
